import {
  bulkProductErrorFragment,
  bulkStockErrorFragment,
  exportErrorFragment,
  productErrorFragment,
  productErrorWithAttributesFragment,
  stockErrorFragment
} from "@saleor/fragments/errors";
import {
  exportFileFragment,
  fragmentVariant,
  productFragmentDetails
} from "@saleor/fragments/products";
import makeMutation from "@saleor/hooks/makeMutation";
import gql from "graphql-tag";

import {
  productBulkDelete,
  productBulkDeleteVariables
} from "./types/productBulkDelete";
import {
  productBulkPublish,
  productBulkPublishVariables
} from "./types/productBulkPublish";
import { ProductCreate, ProductCreateVariables } from "./types/ProductCreate";
import {
  ProductCreateDeeplink,
  ProductCreateDeeplinkVariables
} from "./types/ProductCreateDeeplink";
import { ProductDelete, ProductDeleteVariables } from "./types/ProductDelete";
import { ProductExport, ProductExportVariables } from "./types/ProductExport";
import {
  ProductImageCreate,
  ProductImageCreateVariables
} from "./types/ProductImageCreate";
import {
  ProductImageDelete,
  ProductImageDeleteVariables
} from "./types/ProductImageDelete";
import {
  ProductImageReorder,
  ProductImageReorderVariables
} from "./types/ProductImageReorder";
import {
  ProductImageUpdate,
  ProductImageUpdateVariables
} from "./types/ProductImageUpdate";
import {
  ProductSetAvailabilityForPurchase,
  ProductSetAvailabilityForPurchaseVariables
} from "./types/ProductSetAvailabilityForPurchase";
import {
  productStatusBulkUpdate,
  productStatusBulkUpdateVariables
} from "./types/productStatusBulkUpdate";
import { ProductUpdate, ProductUpdateVariables } from "./types/ProductUpdate";
import { ProductUpload, ProductUploadVariables } from "./types/ProductUpload";
import {
  ProductVariantBulkCreate,
  ProductVariantBulkCreateVariables
} from "./types/ProductVariantBulkCreate";
import {
  ProductVariantBulkDelete,
  ProductVariantBulkDeleteVariables
} from "./types/ProductVariantBulkDelete";
import {
  ProductVariantReorder,
  ProductVariantReorderVariables
} from "./types/ProductVariantReorder";
import {
  ProductVariantSetDefault,
  ProductVariantSetDefaultVariables
} from "./types/ProductVariantSetDefault";
import {
  ProductVisibilityBulkUpdate,
  ProductVisibilityBulkUpdateVariables
} from "./types/ProductVisibilityBulkUpdate";
import {
  setProductVisibility,
  setProductVisibilityVariables
} from "./types/setProductVisibility";
import {
  SimpleProductUpdate,
  SimpleProductUpdateVariables
} from "./types/SimpleProductUpdate";
import {
  SimpleProductVariantUpdate,
  SimpleProductVariantUpdateVariables
} from "./types/SimpleProductVariantUpdate";
import { VariantCreate, VariantCreateVariables } from "./types/VariantCreate";
import { VariantDelete, VariantDeleteVariables } from "./types/VariantDelete";
import {
  VariantImageAssign,
  VariantImageAssignVariables
} from "./types/VariantImageAssign";
import {
  VariantImageUnassign,
  VariantImageUnassignVariables
} from "./types/VariantImageUnassign";
import { VariantUpdate, VariantUpdateVariables } from "./types/VariantUpdate";

export const productImageCreateMutation = gql`
  ${productErrorFragment}
  ${productFragmentDetails}
  mutation ProductImageCreate($product: ID!, $image: Upload!, $alt: String) {
    productImageCreate(input: { alt: $alt, image: $image, product: $product }) {
      errors: productErrors {
        ...ProductErrorFragment
      }
      product {
        ...Product
      }
    }
  }
`;
export const useProductImageCreateMutation = makeMutation<
  ProductImageCreate,
  ProductImageCreateVariables
>(productImageCreateMutation);

export const productDeleteMutation = gql`
  ${productErrorFragment}
  mutation ProductDelete($id: ID!) {
    productDelete(id: $id) {
      errors: productErrors {
        ...ProductErrorFragment
      }
      product {
        id
      }
    }
  }
`;
export const useProductDeleteMutation = makeMutation<
  ProductDelete,
  ProductDeleteVariables
>(productDeleteMutation);

export const productImagesReorder = gql`
  ${productErrorFragment}
  mutation ProductImageReorder($productId: ID!, $imagesIds: [ID]!) {
    productImageReorder(productId: $productId, imagesIds: $imagesIds) {
      errors: productErrors {
        ...ProductErrorFragment
      }
      product {
        id
        images {
          id
          alt
          sortOrder
          url
        }
      }
    }
  }
`;
export const useProductImagesReorder = makeMutation<
  ProductImageReorder,
  ProductImageReorderVariables
>(productImagesReorder);

const productVariantSetDefault = gql`
  ${productErrorFragment}
  ${productFragmentDetails}
  mutation ProductVariantSetDefault($productId: ID!, $variantId: ID!) {
    productVariantSetDefault(productId: $productId, variantId: $variantId) {
      errors: productErrors {
        ...ProductErrorFragment
      }
      product {
        ...Product
      }
    }
  }
`;

export const useProductVariantSetDefaultMutation = makeMutation<
  ProductVariantSetDefault,
  ProductVariantSetDefaultVariables
>(productVariantSetDefault);

export const productUpdateMutation = gql`
  ${productErrorWithAttributesFragment}
  ${productFragmentDetails}
  mutation ProductUpdate($id: ID!, $input: ProductUpdateInput!) {
    productUpdate(id: $id, input: $input) {
      errors: productErrors {
        ...ProductErrorWithAttributesFragment
      }
      product {
        ...Product
      }
    }
  }
`;
export const useProductUpdateMutation = makeMutation<
  ProductUpdate,
  ProductUpdateVariables
>(productUpdateMutation);

export const simpleProductUpdateMutation = gql`
  ${productErrorWithAttributesFragment}
  ${productFragmentDetails}
  mutation SimpleProductUpdate($id: ID!, $input: ProductUpdateInput!) {
    productUpdate(id: $id, input: $input) {
      errors: productErrors {
        ...ProductErrorWithAttributesFragment
      }
      product {
        ...Product
      }
    }
  }
`;
export const useSimpleProductUpdateMutation = makeMutation<
  SimpleProductUpdate,
  SimpleProductUpdateVariables
>(simpleProductUpdateMutation);

export const simpleProductVariantUpdateMutation = gql`
  ${bulkStockErrorFragment}
  ${stockErrorFragment}
  ${fragmentVariant}
  mutation SimpleProductVariantUpdate(
    $productVariantId: ID!
    $addStocks: [StockInput!]!
    $deleteStocks: [ID!]!
    $updateStocks: [StockInput!]!
  ) {
    productVariantStocksCreate(
      stocks: $addStocks
      variantId: $productVariantId
    ) {
      errors: bulkStockErrors {
        ...BulkStockErrorFragment
      }
      productVariant {
        ...ProductVariant
      }
    }
    productVariantStocksDelete(
      warehouseIds: $deleteStocks
      variantId: $productVariantId
    ) {
      errors: stockErrors {
        ...StockErrorFragment
      }
      productVariant {
        ...ProductVariant
      }
    }
    productVariantStocksUpdate(
      stocks: $updateStocks
      variantId: $productVariantId
    ) {
      errors: bulkStockErrors {
        ...BulkStockErrorFragment
      }
      productVariant {
        ...ProductVariant
      }
    }
  }
`;
export const useSimpleProductVariantUpdateMutation = makeMutation<
  SimpleProductVariantUpdate,
  SimpleProductVariantUpdateVariables
>(simpleProductVariantUpdateMutation);

export const productCreateMutation = gql`
  ${productErrorWithAttributesFragment}
  ${productFragmentDetails}
  mutation ProductCreate($input: ProductCreateInput!) {
    productCreate(input: $input) {
      errors: productErrors {
        ...ProductErrorWithAttributesFragment
      }
      product {
        ...Product
      }
    }
  }
`;
export const useProductCreateMutation = makeMutation<
  ProductCreate,
  ProductCreateVariables
>(productCreateMutation);

export const variantDeleteMutation = gql`
  ${productErrorFragment}
  mutation VariantDelete($id: ID!) {
    productVariantDelete(id: $id) {
      errors: productErrors {
        ...ProductErrorFragment
      }
      productVariant {
        id
      }
    }
  }
`;
export const useVariantDeleteMutation = makeMutation<
  VariantDelete,
  VariantDeleteVariables
>(variantDeleteMutation);

export const variantUpdateMutation = gql`
  ${bulkStockErrorFragment}
  ${fragmentVariant}
  ${productErrorWithAttributesFragment}
  mutation VariantUpdate(
    $addStocks: [StockInput!]!
    $removeStocks: [ID!]!
    $id: ID!
    $brand: ID
    $costPrice: PositiveDecimal
    $price: PositiveDecimal
    $sku: String
    $trackInventory: Boolean!
    $stocks: [StockInput!]!
    $weight: WeightScalar
    $width: DistanceScalar
    $length: DistanceScalar
    $height: DistanceScalar
  ) {
    productVariantUpdate(
      id: $id
      input: {
        brand: $brand
        costPrice: $costPrice
        price: $price
        sku: $sku
        trackInventory: $trackInventory
        weight: $weight
        width: $width
        length: $length
        height: $height
      }
    ) {
      errors: productErrors {
        ...ProductErrorWithAttributesFragment
      }
      productVariant {
        ...ProductVariant
      }
    }
    productVariantStocksUpdate(stocks: $stocks, variantId: $id) {
      errors: bulkStockErrors {
        ...BulkStockErrorFragment
      }
      productVariant {
        ...ProductVariant
      }
    }
    productVariantStocksCreate(stocks: $addStocks, variantId: $id) {
      errors: bulkStockErrors {
        ...BulkStockErrorFragment
      }
      productVariant {
        id
        stocks {
          ...StockFragment
        }
      }
    }
    productVariantStocksDelete(warehouseIds: $removeStocks, variantId: $id) {
      errors: stockErrors {
        code
        field
      }
      productVariant {
        id
        stocks {
          ...StockFragment
        }
      }
    }
  }
`;
export const useVariantUpdateMutation = makeMutation<
  VariantUpdate,
  VariantUpdateVariables
>(variantUpdateMutation);

export const variantCreateMutation = gql`
  ${fragmentVariant}
  ${productErrorWithAttributesFragment}
  mutation VariantCreate($input: ProductVariantCreateInput!) {
    productVariantCreate(input: $input) {
      errors: productErrors {
        ...ProductErrorWithAttributesFragment
      }
      productVariant {
        ...ProductVariant
      }
    }
  }
`;
export const useVariantCreateMutation = makeMutation<
  VariantCreate,
  VariantCreateVariables
>(variantCreateMutation);

export const productImageDeleteMutation = gql`
  ${productErrorFragment}
  mutation ProductImageDelete($id: ID!) {
    productImageDelete(id: $id) {
      errors: productErrors {
        ...ProductErrorFragment
      }
      product {
        id
        images {
          id
        }
      }
    }
  }
`;
export const useProductImageDeleteMutation = makeMutation<
  ProductImageDelete,
  ProductImageDeleteVariables
>(productImageDeleteMutation);

export const productImageUpdateMutation = gql`
  ${productErrorFragment}
  ${productFragmentDetails}
  mutation ProductImageUpdate($id: ID!, $alt: String!) {
    productImageUpdate(id: $id, input: { alt: $alt }) {
      errors: productErrors {
        ...ProductErrorFragment
      }
      product {
        ...Product
      }
    }
  }
`;
export const useProductImageUpdateMutation = makeMutation<
  ProductImageUpdate,
  ProductImageUpdateVariables
>(productImageUpdateMutation);

export const variantImageAssignMutation = gql`
  ${fragmentVariant}
  ${productErrorFragment}
  mutation VariantImageAssign($variantId: ID!, $imageId: ID!) {
    variantImageAssign(variantId: $variantId, imageId: $imageId) {
      errors: productErrors {
        ...ProductErrorFragment
      }
      productVariant {
        ...ProductVariant
      }
    }
  }
`;
export const useVariantImageAssignMutation = makeMutation<
  VariantImageAssign,
  VariantImageAssignVariables
>(variantImageAssignMutation);

export const variantImageUnassignMutation = gql`
  ${fragmentVariant}
  ${productErrorFragment}
  mutation VariantImageUnassign($variantId: ID!, $imageId: ID!) {
    variantImageUnassign(variantId: $variantId, imageId: $imageId) {
      errors: productErrors {
        ...ProductErrorFragment
      }
      productVariant {
        ...ProductVariant
      }
    }
  }
`;
export const useVariantImageUnassignMutation = makeMutation<
  VariantImageUnassign,
  VariantImageUnassignVariables
>(variantImageUnassignMutation);

export const productBulkDeleteMutation = gql`
  ${productErrorFragment}
  mutation productBulkDelete($ids: [ID!]!) {
    productBulkDelete(ids: $ids) {
      errors: productErrors {
        ...ProductErrorFragment
      }
    }
  }
`;
export const useProductBulkDeleteMutation = makeMutation<
  productBulkDelete,
  productBulkDeleteVariables
>(productBulkDeleteMutation);

export const productBulkPublishMutation = gql`
  ${productErrorFragment}
  mutation productBulkPublish($ids: [ID!]!, $isPublished: Boolean!) {
    productBulkPublish(ids: $ids, isPublished: $isPublished) {
      errors: productErrors {
        ...ProductErrorFragment
      }
    }
  }
`;
export const useProductBulkPublishMutation = makeMutation<
  productBulkPublish,
  productBulkPublishVariables
>(productBulkPublishMutation);

export const productStatusBulkUpdateMutation = gql`
  mutation productStatusBulkUpdate(
    $ids: [ID!]!
    $productStatus: ProductStatusEnum!
  ) {
    productStatusBulkUpdate(ids: $ids, productStatus: $productStatus) {
      count
      errors {
        field
        message
      }
    }
  }
`;
export const useProductBulkReadyMutation = makeMutation<
  productStatusBulkUpdate,
  productStatusBulkUpdateVariables
>(productStatusBulkUpdateMutation);

export const ProductVariantBulkCreateMutation = gql`
  ${bulkProductErrorFragment}
  mutation ProductVariantBulkCreate(
    $id: ID!
    $inputs: [ProductVariantBulkCreateInput]!
  ) {
    productVariantBulkCreate(product: $id, variants: $inputs) {
      errors: bulkProductErrors {
        ...BulkProductErrorFragment
      }
    }
  }
`;
export const useProductVariantBulkCreateMutation = makeMutation<
  ProductVariantBulkCreate,
  ProductVariantBulkCreateVariables
>(ProductVariantBulkCreateMutation);

export const ProductVariantBulkDeleteMutation = gql`
  ${productErrorFragment}
  mutation ProductVariantBulkDelete($ids: [ID!]!, $brand: ID) {
    productVariantBulkDelete(ids: $ids, brand: $brand) {
      errors: productErrors {
        ...ProductErrorFragment
      }
    }
  }
`;
export const useProductVariantBulkDeleteMutation = makeMutation<
  ProductVariantBulkDelete,
  ProductVariantBulkDeleteVariables
>(ProductVariantBulkDeleteMutation);

export const productExportMutation = gql`
  ${exportFileFragment}
  ${exportErrorFragment}
  mutation ProductExport($input: ExportProductsInput!) {
    exportProducts(input: $input) {
      exportFile {
        ...ExportFileFragment
      }
      errors: exportErrors {
        ...ExportErrorFragment
      }
    }
  }
`;
export const useProductExport = makeMutation<
  ProductExport,
  ProductExportVariables
>(productExportMutation);

const productSetAvailabilityForPurchase = gql`
  ${productErrorFragment}
  mutation ProductSetAvailabilityForPurchase(
    $isAvailable: Boolean!
    $productId: ID!
    $startDate: Date
  ) {
    productSetAvailabilityForPurchase(
      isAvailable: $isAvailable
      productId: $productId
      startDate: $startDate
    ) {
      product {
        id
        availableForPurchase
        isAvailable
        isAvailableForPurchase
      }
      errors: productErrors {
        ...ProductErrorFragment
        message
      }
    }
  }
`;

export const useProductSetAvailabilityForPurchase = makeMutation<
  ProductSetAvailabilityForPurchase,
  ProductSetAvailabilityForPurchaseVariables
>(productSetAvailabilityForPurchase);

const productVariantReorder = gql`
  ${productErrorFragment}
  ${productFragmentDetails}
  mutation ProductVariantReorder($move: ReorderInput!, $productId: ID!) {
    productVariantReorder(moves: [$move], productId: $productId) {
      errors: productErrors {
        ...ProductErrorFragment
      }
      product {
        ...Product
      }
    }
  }
`;
export const useProductVariantReorderMutation = makeMutation<
  ProductVariantReorder,
  ProductVariantReorderVariables
>(productVariantReorder);

const ProductVisibilityMutation = gql`
  mutation setProductVisibility($input: ProductVisibilityInput!) {
    setProductVisibility(input: $input) {
      productVisibility {
        id
        isVisible
        isAvailable
      }
      productErrors {
        field
        message
      }
    }
  }
`;
export const useProductVisibilityMutation = makeMutation<
  setProductVisibility,
  setProductVisibilityVariables
>(ProductVisibilityMutation);

const ProductVisibilityBulkUpdateMutation = gql`
  mutation ProductVisibilityBulkUpdate($ids: [ID]!, $isVisible: Boolean!) {
    productVisibilityBulkUpdate(ids: $ids, isVisible: $isVisible) {
      count
      productVisibility {
        id
        isVisible
        isAvailable
      }
      productErrors {
        message
        field
      }
    }
  }
`;
export const useProductVisibilityBulkUpdateMutation = makeMutation<
  ProductVisibilityBulkUpdate,
  ProductVisibilityBulkUpdateVariables
>(ProductVisibilityBulkUpdateMutation);

const productVisibility = gql`
  mutation ProductVisibility($input: ProductVisibilityInput!) {
    setProductVisibility(input: $input) {
      productErrors {
        field
        message
        code
      }
      productVisibility {
        id
        isVisible
        isAvailable
      }
    }
  }
`;

export const useProductSetVisibility = makeMutation<
  setProductVisibility,
  setProductVisibilityVariables
>(productVisibility);

const productUpload = gql`
  mutation ProductUpload(
    $file: Upload!
    $overwrite: Boolean!
    $templateType: ProductUploadTemplateTypeEnum!
    $uploadAs: ProductUserViewEnum
    $brand: ID
  ) {
    productUploadTemplate(
      input: {
        file: $file
        isOverwrite: $overwrite
        templateType: $templateType
        uploadAs: $uploadAs
        brand: $brand
      }
    ) {
      productErrors {
        field
        code
        message
      }
      productUpload {
        id
        status
        errorFileUrl
        message
        user {
          id
        }
      }
    }
  }
`;
export const useProductUpload = makeMutation<
  ProductUpload,
  ProductUploadVariables
>(productUpload);

const productCreateDeeplink = gql`
  mutation ProductCreateDeeplink($id: ID!) {
    productGenerateDynamicLink(id: $id) {
      productErrors {
        field
        code
        message
      }
      product {
        id
        deeplinkUrl
        shortDynamicLink
      }
    }
  }
`;
export const useProductCreateDeeplink = makeMutation<
  ProductCreateDeeplink,
  ProductCreateDeeplinkVariables
>(productCreateDeeplink);
